import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { codeRepoAPI } from '../services/api';

function CodeRepoProgressList() {
  const { repoId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('students'); // students or matrix
  const [repoData, setRepoData] = useState(null);
  const [studentsProgress, setStudentsProgress] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user && user.role !== 'admin') {
      setError('Access denied. Admin only.');
      navigate('/code-repos');
      return;
    }
    fetchProgressData();
  }, [repoId, user, navigate]);

  const fetchProgressData = async () => {
    try {
      setLoading(true);
      const response = await codeRepoAPI.getStudentProgress(repoId);
      setRepoData(response.data.repo);
      setStudentsProgress(response.data.studentsProgress);
      setError('');
    } catch (err) {
      console.error('Error fetching progress:', err);
      setError(err.response?.data?.message || 'Failed to fetch progress data');
    } finally {
      setLoading(false);
    }
  };

  const getProgressColor = useCallback((percentage) => {
    if (percentage === 100) return '#22c55e';
    if (percentage >= 75) return '#3b82f6';
    if (percentage >= 50) return '#f59e0b';
    if (percentage >= 25) return '#f97316';
    return '#ef4444';
  }, []);

  const filteredStudents = useMemo(() => {
    return studentsProgress
      .filter(student => {
        if (!searchQuery) return true;
        const searchLower = searchQuery.toLowerCase();
        return student.studentName.toLowerCase().includes(searchLower) ||
               student.studentEmail.toLowerCase().includes(searchLower);
      })
      .sort((a, b) => a.studentName.localeCompare(b.studentName));
  }, [studentsProgress, searchQuery]);

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '20px' }}>
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#667eea', fontSize: '16px' }}>Loading progress data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ paddingTop: '20px' }}>
        <div className="card">
          <div className="error">{error}</div>
          <button 
            onClick={() => navigate('/code-repos')}
            className="btn btn-secondary"
          >
            ← Back to Code Repos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '20px', background: '#ffffff', minHeight: '100vh', borderRadius: '12px' }}>
      {/* Header */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <button 
              onClick={() => navigate('/code-repos')}
              className="btn btn-secondary"
              style={{ marginBottom: '12px', fontSize: '13px', padding: '6px 12px' }}
            >
              ← Back to Code Repos
            </button>
            <h2 style={{ color: '#667eea', fontSize: '24px', marginBottom: '8px' }}>
              📊 Progress Tracking
            </h2>
            <p style={{ color: '#718096', fontSize: '14px', margin: 0 }}>
              {repoData?.title} ({repoData?.repoCode})
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '20px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <button
          onClick={() => setActiveTab('students')}
          style={{
            padding: '12px 24px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'students' ? '2px solid #667eea' : '2px solid transparent',
            color: activeTab === 'students' ? '#667eea' : '#718096',
            fontWeight: activeTab === 'students' ? '600' : '400',
            fontSize: '15px',
            cursor: 'pointer',
            marginBottom: '-2px',
            transition: 'all 0.2s'
          }}
        >
          👥 Student-wise Progress
        </button>
        <button
          onClick={() => setActiveTab('matrix')}
          style={{
            padding: '12px 24px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'matrix' ? '2px solid #667eea' : '2px solid transparent',
            color: activeTab === 'matrix' ? '#667eea' : '#718096',
            fontWeight: activeTab === 'matrix' ? '600' : '400',
            fontSize: '15px',
            cursor: 'pointer',
            marginBottom: '-2px',
            transition: 'all 0.2s'
          }}
        >
          📋 Matrix View
        </button>
      </div>

      {/* Student-wise Progress Tab */}
      {activeTab === 'students' && (
        <div>
          {/* Search Bar */}
          <div className="card" style={{ marginBottom: '20px' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search students by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px 12px 40px',
                  fontSize: '14px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  outline: 'none'
                }}
              />
              <span style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '16px',
                color: '#a0aec0'
              }}>🔍</span>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    fontSize: '18px',
                    color: '#a0aec0',
                    cursor: 'pointer'
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {filteredStudents.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ fontSize: '36px', margin: '0 0 12px 0' }}>
                {searchQuery ? '🔍' : '👥'}
              </p>
              <p style={{ color: '#718096', margin: 0 }}>
                {searchQuery 
                  ? `No students found matching "${searchQuery}"`
                  : 'No students enrolled in this repository'}
              </p>
            </div>
          ) : (
            <>
              {/* Student Cards */}
              <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
                {filteredStudents.map(student => (
                  <div
                    key={student.studentId}
                    onClick={() => navigate(`/code-repos/${repoId}/student/${student.studentId}/progress`)}
                    className="card"
                    style={{
                      padding: '20px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '20px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <h4 style={{
                        margin: '0 0 4px 0',
                        fontSize: '16px',
                        color: '#667eea',
                        fontWeight: '600'
                      }}>
                        👤 {student.studentName}
                      </h4>
                      <p style={{
                        margin: '0',
                        fontSize: '13px',
                        color: '#718096'
                      }}>
                        {student.studentEmail}
                      </p>
                    </div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      minWidth: '200px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '6px'
                        }}>
                          <span style={{
                            fontSize: '12px',
                            color: '#718096',
                            fontWeight: '500'
                          }}>
                            Progress
                          </span>
                          <span style={{
                            fontSize: '14px',
                            fontWeight: '700',
                            color: getProgressColor(student.percentageComplete)
                          }}>
                            {student.percentageComplete}%
                          </span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '8px',
                          background: '#e2e8f0',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${student.percentageComplete}%`,
                            height: '100%',
                            background: getProgressColor(student.percentageComplete),
                            transition: 'width 0.3s ease',
                            borderRadius: '4px'
                          }} />
                        </div>
                        <p style={{
                          margin: '4px 0 0 0',
                          fontSize: '11px',
                          color: '#a0aec0',
                          textAlign: 'right'
                        }}>
                          {student.completedCount} / {student.totalAssignments} completed
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary Stats */}
              <div className="card" style={{
                padding: '20px',
                background: '#f7fafc',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '20px'
              }}>
                <div>
                  <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#718096', fontWeight: '500' }}>
                    {searchQuery ? 'Showing / Total Students' : 'Total Students'}
                  </p>
                  <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#1a202c' }}>
                    {searchQuery ? (
                      <>
                        {filteredStudents.length} / {studentsProgress.length}
                      </>
                    ) : (
                      studentsProgress.length
                    )}
                  </p>
                </div>
                <div>
                  <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#718096', fontWeight: '500' }}>
                    Total Assignments
                  </p>
                  <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#1a202c' }}>
                    {studentsProgress[0]?.totalAssignments || 0}
                  </p>
                </div>
                <div>
                  <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#718096', fontWeight: '500' }}>
                    Average Completion
                  </p>
                  <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#667eea' }}>
                    {studentsProgress.length > 0
                      ? Math.round(
                          studentsProgress.reduce((acc, s) => acc + s.percentageComplete, 0) /
                            studentsProgress.length
                        )
                      : 0}%
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Matrix View Tab */}
      {activeTab === 'matrix' && (
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '18px', marginBottom: '8px', color: '#1a202c' }}>
              📋 Assignment Completion Matrix
            </h3>
            <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
              View which students have completed which assignments in a grid format.
            </p>
          </div>
          <button
            onClick={() => navigate(`/code-repos/${repoId}/progress-matrix`)}
            className="btn btn-primary"
            style={{ fontSize: '14px', padding: '10px 20px' }}
          >
            View Full Matrix →
          </button>
        </div>
      )}
    </div>
  );
}

export default memo(CodeRepoProgressList);
